import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronRight, Shield, Clock, Headphones, Zap } from 'lucide-react';

// Hero carousel slides data - Obelisco Care FIRST for A/B test (conversion focus)
const heroSlides = [
  {
    id: 1,
    badge: "Plano de Proteção Exclusivo",
    title: "Obelisco Care",
    titleHighlight: "Tranquilidade Total",
    subtitle: "Subscreva um plano mensal e tenha acesso prioritário, manutenção preventiva e horas de serviço incluídas. A partir de 349€/mês + IVA.",
    ctaPrimary: { text: "Ver Planos", href: "#obelisco-care" },
    ctaSecondary: { text: "Saber Mais", href: "#vantagens" },
    image: "https://static.prod-images.emergentagent.com/jobs/cbda47ad-351b-4c1c-8f47-fda2fc28d16b/images/04e69270deed11d1760bbc2bed8e9ec6a50ceca3bec2a63a32a16d4bf95abc05.jpeg",
    features: [
      { icon: Shield, text: "Cobertura Total" },
      { icon: Clock, text: "Resposta 24h" },
      { icon: Headphones, text: "Suporte Dedicado" }
    ],
    isPromo: true
  },
  {
    id: 2,
    badge: "Infraestruturas & Energia",
    title: "Engenharia Elétrica & Telecomunicações",
    titleHighlight: "de Elevada Exigência",
    subtitle: "Soluções técnicas integradas com rigor, precisão e total conformidade com normas europeias.",
    ctaPrimary: { text: "Solicitar Proposta", href: "#contact" },
    ctaSecondary: { text: "Ver Serviços", href: "#services" },
    image: "https://static.prod-images.emergentagent.com/jobs/cbda47ad-351b-4c1c-8f47-fda2fc28d16b/images/a4ce54901cc02dc5ef81d5d24fc71ec3c9323ca2261212f90a48d2bc6bcf7452.jpeg"
  },
  {
    id: 3,
    badge: "Capacidade Operacional",
    title: "Frota Técnica Especializada",
    titleHighlight: "Pronta a Intervir",
    subtitle: "Veículos equipados e equipas qualificadas para assistência rápida e eficaz em toda a Grande Lisboa.",
    ctaPrimary: { text: "Contactar Agora", href: "#contact" },
    ctaSecondary: null,
    image: "https://static.prod-images.emergentagent.com/jobs/cbda47ad-351b-4c1c-8f47-fda2fc28d16b/images/ae660fd04f977640891de749396518532fd9377b2b16302c0ee1063f81759411.jpeg"
  },
  {
    id: 4,
    badge: "Qualidade & Segurança",
    title: "Padrões Industriais de",
    titleHighlight: "Alta Performance",
    subtitle: "Compromisso com a segurança, fiabilidade dos materiais e execução técnica sem falhas.",
    ctaPrimary: { text: "Ver Certificações", href: "#vantagens" },
    ctaSecondary: null,
    image: "https://static.prod-images.emergentagent.com/jobs/cbda47ad-351b-4c1c-8f47-fda2fc28d16b/images/3c91a2ae724f73b2391b8bbec25a56d43eaee98eceb0c06b921ed2da77fa47a9.jpeg"
  }
];

const SLIDE_DURATION = 6000; // 6 seconds per slide

const HeroCarousel = ({ scrollToSection }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);

  // Auto-advance slides
  useEffect(() => {
    if (isPaused) return;

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          setCurrentSlide(current => (current + 1) % heroSlides.length);
          return 0;
        }
        return prev + (100 / (SLIDE_DURATION / 50));
      });
    }, 50);

    return () => clearInterval(progressInterval);
  }, [isPaused, currentSlide]);

  // Reset progress when slide changes
  useEffect(() => {
    setProgress(0);
  }, [currentSlide]);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
    setProgress(0);
  }, []);

  const handleCTAClick = useCallback((href) => {
    if (href.startsWith('#')) {
      const sectionId = href.substring(1);
      if (scrollToSection) {
        scrollToSection(sectionId);
      } else {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [scrollToSection]);

  const currentData = heroSlides[currentSlide];

  return (
    <section
      id="hero"
      className="relative h-[600px] md:h-[650px] w-full overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="hero-carousel"
    >
      {/* Background Images with Crossfade */}
      <AnimatePresence mode="sync">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${currentData.image})` }}
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/30" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={`content-${currentSlide}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                {/* Badge - with shimmer effect for promo slides */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="mb-6"
                >
                  <span className={`relative inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold uppercase tracking-wider overflow-hidden ${
                    currentData.isPromo 
                      ? 'border-yellow-400/60 bg-gradient-to-r from-yellow-500/20 via-yellow-400/30 to-yellow-500/20 text-yellow-300' 
                      : 'border-yellow-500/40 bg-yellow-400/10 text-yellow-400'
                  } backdrop-blur-sm`}>
                    {/* Shimmer effect for promo badge */}
                    {currentData.isPromo && (
                      <span className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                    )}
                    <span className={`h-2 w-2 rounded-full ${currentData.isPromo ? 'bg-yellow-300 animate-ping' : 'bg-yellow-400 animate-pulse'}`} />
                    <span className="relative z-10">{currentData.badge}</span>
                    {currentData.isPromo && (
                      <Zap className="h-4 w-4 text-yellow-300 animate-pulse" />
                    )}
                  </span>
                </motion.div>

                {/* Title */}
                <motion.h1
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight tracking-tight text-white"
                >
                  {currentData.title}{' '}
                  <span className="text-yellow-400">{currentData.titleHighlight}</span>
                </motion.h1>

                {/* Subtitle */}
                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="mt-6 text-lg sm:text-xl text-zinc-300 leading-relaxed max-w-2xl"
                >
                  {currentData.subtitle}
                </motion.p>

                {/* Obelisco Care Features - Special promo slide */}
                {currentData.isPromo && currentData.features && (
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.35 }}
                    className="mt-6 flex flex-wrap gap-4"
                  >
                    {currentData.features.map((feature, idx) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <div
                          key={idx}
                          className="inline-flex items-center gap-2 rounded-xl border border-yellow-500/30 bg-yellow-400/5 backdrop-blur-sm px-4 py-2"
                        >
                          <FeatureIcon className="h-5 w-5 text-yellow-400" />
                          <span className="text-sm font-medium text-white">{feature.text}</span>
                        </div>
                      );
                    })}
                  </motion.div>
                )}

                {/* CTAs */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="mt-10 flex flex-col sm:flex-row gap-4"
                >
                  <button
                    onClick={() => handleCTAClick(currentData.ctaPrimary.href)}
                    className="group inline-flex items-center justify-center gap-2 rounded-xl bg-yellow-400 px-8 py-4 text-base font-bold uppercase tracking-wide text-slate-950 transition-all duration-300 hover:bg-yellow-300 hover:shadow-lg hover:shadow-yellow-400/25"
                    data-testid="hero-cta-primary"
                  >
                    {currentData.ctaPrimary.text}
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </button>

                  {currentData.ctaSecondary && (
                    <button
                      onClick={() => handleCTAClick(currentData.ctaSecondary.href)}
                      className="group inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-600 bg-zinc-900/50 backdrop-blur-sm px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:border-yellow-500/50 hover:bg-zinc-800/50"
                      data-testid="hero-cta-secondary"
                    >
                      {currentData.ctaSecondary.text}
                      <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </button>
                  )}
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Progress Bars Navigation */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {heroSlides.map((slide, index) => (
          <button
            key={slide.id}
            onClick={() => goToSlide(index)}
            className="group relative h-1 w-16 sm:w-24 rounded-full bg-white/20 overflow-hidden transition-all hover:bg-white/30"
            aria-label={`Ir para slide ${index + 1}`}
            data-testid={`hero-progress-${index}`}
          >
            {/* Progress fill */}
            <div
              className="absolute inset-y-0 left-0 bg-yellow-400 rounded-full transition-all duration-100"
              style={{
                width: index === currentSlide ? `${progress}%` : index < currentSlide ? '100%' : '0%'
              }}
            />
            {/* Hover indicator */}
            <div className="absolute inset-0 bg-yellow-400/0 group-hover:bg-yellow-400/30 transition-colors" />
          </button>
        ))}
      </div>

      {/* Slide Counter */}
      <div className="absolute bottom-8 right-8 z-20 hidden sm:flex items-center gap-2 text-sm font-medium text-zinc-400">
        <span className="text-yellow-400 text-lg font-bold">0{currentSlide + 1}</span>
        <span>/</span>
        <span>0{heroSlides.length}</span>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-yellow-400/5 to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-slate-950 to-transparent pointer-events-none" />
    </section>
  );
};

export default HeroCarousel;
